import { useCallback, useContext } from 'react';
import _ from 'underscore';

import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, getWizardData, getFlowFactors } from '@bingads-webui-fluent-component/wizard';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import {
  readCampaign,
  readAdGroup,
  readCampaignCriterions,
  createOrUpdateCampaign,
  mapBulkUpsertErrors,
  getBulkUploadErrorType,
  ERROR_ENTITY,
  genericApiError,
  filterCriterionsByType,
  LocationCriterionTypes,
  AudienceCriterionTypes,
  AgeCriterionTypes,
  GenderCriterionTypes
} from '@bingads-webui-campaign-react/linkedin-data';
import {
  useTargetingLocationsAdapter,
  useTargetingAudiencesAdapter,
  useTargetingAgeAdapter,
  useTargetingGenderAdapter,
  useCampaignConversionGoalsAdapter,
} from '@bingads-webui-campaign-react/linkedin-targeting';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
};

export const useSummaryRepository = () => {
  const i18n = useContext(I18nContext);

  const { CurrentAccount: { CurrencyCode } } = useContext(ServerContext);
  const { scope: { values: { cid, aid } } } = useContext(ScopeContext);

  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInSummaryStep',
    activityName: 'Repository',
  });

  const { state } = useWizardService();
  const {
    campaignId,
    adGroupId,
    connectedCompanyPage,
    ads,
    hasLinkedInCompatibleGoals,
  } = getWizardData(state);
  const flowFactors = getFlowFactors(state);
  const isMultiAdModel = _.get(flowFactors, ['isMultiAdModel'], false);

  const { mapToViewModel: mapLocationsToViewModel } = useTargetingLocationsAdapter({ campaignId });
  const { mapToViewModel: mapAudiencesToViewModel } = useTargetingAudiencesAdapter({ campaignId });
  const { mapToViewModel: mapAgeRangesToViewModel } = useTargetingAgeAdapter({ campaignId });
  const { mapToViewModel: mapGendersToViewModel } = useTargetingGenderAdapter({ campaignId });
  const { mapToViewModel: mapConversionGoalsToViewModel } = useCampaignConversionGoalsAdapter({ campaignId });

  const load = useCallback(() => {
    const loadCampaign = () => readCampaign(cid, aid, campaignId);
    const loadAdGroup = () => readAdGroup(cid, aid, campaignId, adGroupId);
    const loadCampaignCriterions = () => readCampaignCriterions(cid, aid, campaignId);

    const initialRequests = [
      loadCampaign(),
      loadAdGroup(),
      loadCampaignCriterions(),
    ];

    return Promise.all(initialRequests)
      .then(([
        campaign,
        adGroup,
        criterionsResponse,
      ]) => {
        const criterions = _.get(criterionsResponse, 'value', []);

        const locationCriterions = filterCriterionsByType(criterions, LocationCriterionTypes);
        const audienceCriterions = filterCriterionsByType(criterions, AudienceCriterionTypes);
        const ageCriterions = filterCriterionsByType(criterions, AgeCriterionTypes);
        const genderCriterions = filterCriterionsByType(criterions, GenderCriterionTypes);
        const conversionGoals = {
          goals: mapConversionGoalsToViewModel(_.get(campaign, 'CampaignConversionGoal.Goals', [])),
          hasLinkedInCompatibleGoals,
        };

        return ({
          campaign,
          adGroup,
          companyInfo: connectedCompanyPage,
          locationTargets: mapLocationsToViewModel(locationCriterions),
          audienceTargets: mapAudiencesToViewModel(audienceCriterions),
          ageTargets: mapAgeRangesToViewModel(ageCriterions),
          genderTargets: mapGendersToViewModel(genderCriterions),
          currencyCode: CurrencyCode,
          numAds: _.size(ads),
          isMultiAdModel,
          numGoals: _.size(conversionGoals.goals),
          conversionGoals,
        });
      })
      .catch((error) => {
        logger.error(`Failed to load summary data: ${JSON.stringify(error)}`);
        throw error;
      });
  }, [cid, aid, campaignId, adGroupId, connectedCompanyPage, ads, isMultiAdModel, logger, mapLocationsToViewModel, mapAudiencesToViewModel, mapAgeRangesToViewModel, mapGendersToViewModel, CurrencyCode, mapConversionGoalsToViewModel, hasLinkedInCompatibleGoals]);

  const save = useCallback(() => {
    const campaign = {
      Id: campaignId,
      Status: 'Active',
      AdGroups: [{
        Id: adGroupId,
        Status: 'Active',
        Campaign: { Id: campaignId },
      }],
    };

    return createOrUpdateCampaign(campaign)
      .then(() => {
        logger.trace('Activated successfully');
        return Promise.resolve();
      })
      .catch((err) => {
        logger.error(`Failed to activate adgroup: ${JSON.stringify(err)}`);

        const errors = _.get(err, 'errors');
        const serverErrors = errors
          ? mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n)
          : [genericApiError];

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: { serverErrors } });
      });
  }, [adGroupId, campaignId, i18n, logger]);

  return {
    load,
    save,
  };
};
